// Packages:
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from "react";
import debounce from "lodash.debounce";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  MiniMap,
  useEdgesState,
  Controls,
  // Position
} from "react-flow-renderer";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import { useDispatch, useSelector } from "react-redux";
import { useDeepCompareEffect } from "react-use";
import {
  Tooltip,
  IconButton,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import DataSaverOnSharpIcon from "@mui/icons-material/DataSaverOnSharp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
// Constants:
import {
  NODE_TYPE,
  WORKFLOW_IS_DRAFT,
  WORKFLOW_IS_PUBLISHED,
  WORKFLOW_IS_UNDER_REVIEW,
} from "../../constants/studioV2";

// Components:
import {
  Assets,
  Configuration,
  Nodes,
  Workflows,
} from "../../components/StudioV2";
import {
  shouldAutosaveWorkflow,
  prepareNodes,
  determineWorkflowStage,
  checkForParallelParent,
  findNodeWithId,
} from "./utils";

import { fetchCommentOnWorkflowAPI, addCommentOnWorkflowAPI } from "./utils/api";
import { CommentBox } from "../../components";

// Redux:
import {  getStudioActions, setSelectedNode } from "../../redux/actions/studioActions";

// Styles:
import {
  Wrapper,
  Title,
  ButtonGroup,
  Canvas,
  LoadingCanvas,
  MainDiv,
  SideBarCollapse
} from "./styles";
import {
  createDraft,
  createEmptyDraft,
  deleteDraft,
  saveDraft,
  submitDraft,
} from "../../lib/workflows/draftsOperations";
import {
  isWorkflowValid,
  loadWorkflowData,
} from "../../lib/workflows/commonOperations";
import { ADMIN, SUPERVISOR, SUPER_ADMIN } from "../../constants/user";
import {
  publishDraft,
  rejectDraft,
} from "../../lib/workflows/reviewOperations";
import { archiveWorkflow } from "../../lib/workflows/publishedOperations";
import { useNavigate, useSearchParams } from "react-router-dom";
import ROUTES from "../../constants/routes";
import InlineEdit from "./utils/InlineEdit";
import { conditionType, operators, variables } from "../../components/StudioV2/RuleBox/utils";
import AutoFLow, { TEMPLATE_AUTOFLOW } from "./Autoflow";

const TooltipIconButton = ({ title, onClick,icon,disabled,color }) => (
          <Tooltip title={title}>
            <IconButton onClick={onClick} disabled={disabled} color={color}>
              {icon}
            </IconButton>
          </Tooltip>
);

const nodeTypes = {
    [NODE_TYPE.start]: Nodes.StartNode,
    [NODE_TYPE.leaf]: Nodes.LeafNode,
    [NODE_TYPE.message]: Nodes.MessageNode,
    [NODE_TYPE.form]: Nodes.FormNode,
    [NODE_TYPE.api]: Nodes.APINode,
    [NODE_TYPE.email]: Nodes.EmailNode,
    [NODE_TYPE.decision]: Nodes.DecisionNode,
    [NODE_TYPE.ticket]: Nodes.TicketNode,
    [NODE_TYPE.shopify]: Nodes.ShopifyNode,
    [NODE_TYPE.google_sheets]: Nodes.GoogleSheetsNode,
    [NODE_TYPE.shipping]: Nodes.ShippingNode,
    [NODE_TYPE.decision]: Nodes.DecisionNode,
    [NODE_TYPE.logistics]:Nodes.LogisticsNode,
    [NODE_TYPE.choice]:Nodes.ChoiceNode,
    [NODE_TYPE.ai]:Nodes.AINode,
    [NODE_TYPE.parallel]:Nodes.ParallelNode,
    [NODE_TYPE.parallelHandler]:Nodes.ParallelHandler,
    [NODE_TYPE.ticketure]:Nodes.TicketureNode,
    [NODE_TYPE.acuity]:Nodes.AcuityNode
  }

  const TEMPLATE_WORKFLOW = {
    workflowId: "",
    orgId: '',
    meta: {
      nodes: [],
      edges: [],
      viewport: {
        x: 0,
        y: 0,
        zoom: 1,
      },
      settings: {},
      status: WORKFLOW_IS_DRAFT,
      isArchived: false,
      owner:  "",
      tags: [],
      trainingPhases: [],
      s3Link: "",
      lastEdited: Date.now(),
      workflowName: "new draft",
      publishedBy: "",
      archivedBy: "",
      validFrom:"",
      validTo:"",
      action:'',
    },
  };

// Functions:
const Studio = () => {
  const [showOpt, setShowOpt] = useState(true);
  const [currentRemoteAutoFlow,setCurrentRemoteAutoFlow]=useState(TEMPLATE_AUTOFLOW)

  // Constants:
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = localStorage.getItem("org_id");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const studioActionsFn = getStudioActions(dispatch);
  const [isAutoFlow,setIsAutoFlow]=useState(false)

  // Ref:
  const reactFlowWrapper = useRef(null);

  // Memo:

  // State:
  const userDetails = useSelector((state) => state.auth.userDetails);
  const agent_type = useSelector((state) => state.content.agent_type);

  console.log("Agent type",agent_type)


  const isAutosaving = useRef(false);
  const workflows = useSelector((state) => state.studio.workflows);
  const loadingWorkflows=useSelector((state) => state.studio.loadingWorkflows);
  const [currentRemoteWorkflow, setCurrentRemoteWorkflow] =
    useState(TEMPLATE_WORKFLOW);
  const [workflowStage, setWorkflowStage] = useState(WORKFLOW_IS_DRAFT);
  const [id, setID] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [name, setName] = useState("Untitled");
  const [currentVersion,setCurrentVersion]=useState(null)
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isCreatingNewWorkflow, setIsCreatingNewWorkflow] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [bit, setBit] = useState(true);
  const [open, setOpen] = useState(false);
  const [workflowComments, setWorkflowComments] = useState([]);
  const [fetchingComments, setFetchingComments] = useState(false);
  const [comments, setComments] = useState([]);
  // for sidebar
  const [openCollapse, setOpenCollapse] = useState(false);

  const createWorkflowUrl = useCallback((workflow) => {
    const queryParams = {
      workflowId: workflow.workflowId,
      owner: workflow.meta?.owner ?? "NA",
      status: workflow.meta?.status ?? "NA",
    };
    setSearchParams(queryParams);
  },[searchParams]);

  const fetchData = useCallback(async () => {
    if (searchParams.get("workflowId"))
      try {
        const workflowId = searchParams.get("workflowId");
        const owner = searchParams.get("owner") ?? "NA";
        const status = searchParams.get("status") ?? "NA";
        const queryParams = {
          workflowId: workflowId,
          owner: owner,
          status: status,
        };

        const workflowdata = await loadWorkflowData(orgId, workflowId, owner, status);  
        const isAutoFlow=workflowdata?.meta?.isAutoFlow || false;
        if(isAutoFlow){
          setCurrentRemoteAutoFlow(workflowdata)
          setIsAutoFlow(true)
        }else{
        setWorkflowStage(determineWorkflowStage(workflowdata));
        setName(workflowdata.meta.workflowName);
        setID(workflowdata.workflowId);
        setCurrentRemoteWorkflow(workflowdata);
        setNodes(() => workflowdata.meta.nodes);
        setEdges(() => workflowdata.meta.edges);
        const queryString = Object.keys(queryParams).map((key) => key + "=" + queryParams[key]).join("&");
        navigate(`${ROUTES.AGENT.WORKFLOWS_V2}?${queryString}`, {
          state: {
            initialWorkflowData: currentRemoteWorkflow,
            isNew: true,
          },
        });
      }
      } catch (error) {
        toast.error("Failed to load Worlflow");
        console.error(error);
      }
  }, [orgId, searchParams, userDetails?.email]);

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  // modified workflow
  const getModifiedWorkflow = useCallback(
    (currentWorkflow, nodes, edges, name) => {
    
      const startNode = nodes.find(node => node.data.label === "START");
      const access = startNode?.data?.access;
      const target = startNode?.data?.target;
      const tags = startNode?.data?.tags;
      const comment = startNode?.data?.comment;
      return {
        ...currentWorkflow,
        orgId: orgId,
        meta: {
          ...currentWorkflow.meta,
          access: access??"private",
          target: target??"agent",
          workflowName: name,
          trainingPhrases: tags??[],
          tags: comment??"",
          nodes: prepareNodes(nodes, edges),
          edges,
        },
      };
     },
    [orgId,name,nodes,edges,currentRemoteWorkflow]
  );

  const getSaveReadyWorkflow = useCallback(
    (modifiedWorkflow) => {
      return {
        ...modifiedWorkflow,
        orgId: orgId,
        meta: {
          ...modifiedWorkflow.meta,
          workflowName: name,
        },
      };
    },
    [name, orgId, reactFlowInstance]
  );
  // Auto save workflow
  const initiateAutosaveWorkflow = async (modifiedWorkflow) => {
    if (isAutosaving.current) return;
    isAutosaving.current = true;
    const workflowContent = getSaveReadyWorkflow(modifiedWorkflow);
    const saveData = await saveDraft(workflowContent);
    setCurrentRemoteWorkflow(saveData);
    isAutosaving.current = false;
  };

  //publish workflow
  const handlePublish = async () => {
    if (!isWorkflowValid(currentRemoteWorkflow)) {
      return;
    }
    if (agent_type.toLowerCase() === SUPERVISOR || agent_type.toLowerCase() === SUPER_ADMIN ||agent_type.toLowerCase() === ADMIN) {
      setIsFetching(true);
      try {
        const workflowToBePublished = {
          ...currentRemoteWorkflow,
          meta: {
            ...currentRemoteWorkflow.meta,
            publishedBy: userDetails?.email ?? "",
            validFrom:Date.now()
          },
        };
        const publishedData = await publishDraft(workflowToBePublished);
        setCurrentVersion(publishedData?.meta?.currentVersion)
        createWorkflowUrl(publishedData);
        setCurrentRemoteWorkflow(publishedData);
        toast.success("Draft published successfully");
        setBit((prev) => !prev);
      } catch (error) {
        toast.error("Failed to publish Draft");
        console.error(error);
      }
      setIsFetching(false);
    } else {
      toast.error("Only admins can Publish a submitted Draft");
    }
  };

  // create new workflow
  const initiateCreateNewWorkflow = useCallback(async () => {
    const tempWorkflow = createEmptyDraft(orgId, userDetails?.email);
    const draft = await createDraft(tempWorkflow);
    createWorkflowUrl(draft);
    setCurrentRemoteWorkflow(draft);
  }, []);
  // delete workflow
  const initiateDeleteWorkflow = async () => {
    if (isDeleting === false) setIsDeleting(true);
    else setIsDeleting(false);
    const currentWorkflow = currentRemoteWorkflow;
    if (currentWorkflow.meta.status === WORKFLOW_IS_DRAFT) {
      await deleteDraft(currentRemoteWorkflow);
    } else if (currentWorkflow.meta.status === WORKFLOW_IS_UNDER_REVIEW) {
      const rejectedWorkflow = await rejectDraft(currentRemoteWorkflow);
      createWorkflowUrl(rejectedWorkflow);
      setCurrentRemoteWorkflow(rejectedWorkflow);
      setBit((prev) => !prev);
      return;
    } else {
      const workflowToBeArchived = {
        ...currentRemoteWorkflow,
        meta: {
          ...currentRemoteWorkflow.meta,
          archivedBy: userDetails?.email ?? "",
          validTo:Date.now()
        },
      };
      await archiveWorkflow(workflowToBeArchived);
    }
    if (workflows.length > 1 && currentWorkflow.workflowId === workflows[0].workflowId) {
      createWorkflowUrl(workflows[1]);
    } else {
      setBit((prev)=>!prev)
    }

    setOpenModal(false);
  };

  const changeCommentsArray = useCallback((val) => {
    setComments(val);
  }, []);

  const handleOpenModal = async () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    changeCommentsArray([]);
    setOpenModal(false);
  };

  const handleDraftSubmission = async () => {
    if (!isWorkflowValid(currentRemoteWorkflow)) {
      return;
    }
    try {
      const savedWorkflow= await saveDraft(currentRemoteWorkflow);
      const newWorkflow = await submitDraft(savedWorkflow);
      createWorkflowUrl(newWorkflow);
      setCurrentRemoteWorkflow(newWorkflow);
      setBit((prev) => !prev);
    } catch (error) {
      toast.error("Failed to submit Draft");
      console.error(error);
    }
  };

  const handleAddComment = useCallback(
    async (comment, changeComment) => {
      const isPublished = currentRemoteWorkflow.meta.status === WORKFLOW_IS_PUBLISHED;
      if (comment !== "" && !isPublished) {
        let newId = comments.length + 1;
        const d = new Date();
        let newDateTime = d.toString().substring(0, 24);
        let newComment = {
          id: newId,
          createdAt: newDateTime,
          comment: comment,
          author: userDetails?.email,
        };
        changeCommentsArray([...comments, newComment]);
        changeComment("");
        addCommentOnWorkflowAPI(orgId, id, comment, userDetails?.email);
        const modifiedWorkflow = getModifiedWorkflow(currentRemoteWorkflow,nodes,edges,name);
        setCurrentRemoteWorkflow(modifiedWorkflow);
        const newWorkflow = getSaveReadyWorkflow(modifiedWorkflow);
        await saveDraft(newWorkflow);
      }
    },
    [
      changeCommentsArray,
      comments,
      currentRemoteWorkflow,
      edges,
      getModifiedWorkflow,
      getSaveReadyWorkflow,
      id,
      name,
      nodes,
      orgId,
      userDetails?.email,
    ]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    changeCommentsArray([]);
  }, [changeCommentsArray]);

  const handleClickOpen = useCallback(async (orgId, workflowId) => {
    setFetchingComments(true);
    setOpen(true);
    const response = await fetchCommentOnWorkflowAPI(orgId, workflowId);
    const comments = response?.comments;
    const newComments = comments?.map((comment) => {
      const createdAt = new Date(comment?.createdAt).toUTCString();
      return { ...comment, createdAt };
    });
    setWorkflowComments(newComments);
    setFetchingComments(false);
  }, []);

  // Effects:
  useEffect(() => {
    (async () => {
      if (orgId) {
       studioActionsFn.fetchWorkflows(orgId,userDetails.email,agent_type)
      }
    })();
  }, [bit, name, isDeleting]);

  useEffect(() => {
    const handleResize = debounce(() => {
      setShowOpt(window.innerWidth <= 1300);
    }, 300);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const updateWorkflowToSave = () => {
    if (reactFlowInstance && !isAutosaving.current) {
      const modifiedWorkflow = getModifiedWorkflow(currentRemoteWorkflow, nodes, edges, name);
      if (determineWorkflowStage(currentRemoteWorkflow) === WORKFLOW_IS_DRAFT && shouldAutosaveWorkflow(currentRemoteWorkflow, modifiedWorkflow)) {
           initiateAutosaveWorkflow(modifiedWorkflow);
      }
    }
  };

  const debounce_workflow = useCallback(debounce(updateWorkflowToSave, 10000), [
    name,
    nodes,
    edges,
    reactFlowInstance,
    getModifiedWorkflow,
    currentRemoteWorkflow,
    initiateAutosaveWorkflow,
  ]);
  useDeepCompareEffect(() => {
    debounce_workflow();
    return () => {
      debounce_workflow.cancel();
    };
  }, [debounce_workflow]);

  const saveWorkflow = (workflow) => {
    const modifiedWorkflow = getModifiedWorkflow(workflow, nodes, edges, name);
    if (determineWorkflowStage(currentRemoteWorkflow) === WORKFLOW_IS_DRAFT)
      initiateAutosaveWorkflow(modifiedWorkflow);
  };
  // Functions:
  const onConnect = useCallback(
    (params) => {
      const sourceNode=findNodeWithId(params.source,nodes);
      const targetNode=findNodeWithId(params.target,nodes)  
      let updatedNodes = [...nodes];
      let branchName = '';
      if (sourceNode.type === 'choice'&&params.sourceHandle!=='choice-node-output-default') {
        if (!sourceNode.data.branch) {
          branchName = `branch_${params.target}`;
          updatedNodes = updatedNodes.map(node => {
            if (node.id === params.source) {
              return { ...node, data: { ...node.data,choices:[...node.data.choices,{condition: conditionType[0],isConstant: true,operator: operators[0],variableType: '',
                variable: "",
                inputValue: "",
                isNegative: "",
                id:"",
                branch:`${branchName}`
              }
              ] } };
            }
            return node;
          });
        } else {
          branchName = sourceNode.data.branch;
        }

        setNodes(updatedNodes);
      } 
      else if(sourceNode.type === 'choice'&&params.sourceHandle==='choice-node-output-default'){
        branchName="default";
      }

      if ((checkForParallelParent(params.source, nodes) && checkForParallelParent(params.target, nodes)) ||
          (sourceNode?.type === 'parallel' && checkForParallelParent(params.target, nodes))||
         (targetNode?.type === 'parallel' && checkForParallelParent(params.source, nodes))
          ){
        toast.error("Edges can not be connected")
        return;
     }
      const handleLabel =sourceNode.type==='choice'?branchName: targetNode.type==='parallelHandler'?"parallelEnd":
        params.sourceHandle === null || params.sourceHandle.substring(params.sourceHandle.length - 4) === "true"? "success"
        : params.sourceHandle.substring(params.sourceHandle.length - 5) === "false"? "failure"
        : "default";
  
      const label = handleLabel;
  
      setEdges((currentEdges) =>
        addEdge({
            ...params,
            animated: false,
            style: { strokeWidth: 3 },
            handleLabel,
            label,
          },
          currentEdges
        )
      );
    },
    [setEdges,nodes]
  );

  const onEdgesDelete = useCallback(params => {
    const sourceNodeId = params?.[0].source;
    // Find the source node
    const sourceNode = findNodeWithId(sourceNodeId, nodes);  
    if (sourceNode.type === 'choice') {
      const branchName = `branch_${params?.[0].target}`;
        const updatedChoices = sourceNode.data.choices.filter(choice => choice.branch !== branchName);
        const updatedNodes = nodes.map(node => {
        if (node.id === sourceNodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              choices: updatedChoices
            }
          };
        }
        return node;
      });
  
      // Update the state with the updated nodes
      setNodes(updatedNodes);
    }
  }, [nodes, setNodes]);

  const makeNodesEquispacedAndCentered = useCallback(() => {
    if (!reactFlowWrapper.current) return;
  
    const spacingX = 250; // Horizontal spacing between nodes
    const spacingY = 150; // Vertical spacing between nodes
  
    const updatedNodes = nodes.map(node => {
      const connectedEdges = edges.filter(edge => edge.source === node.id);
      const connectedNodes = connectedEdges.map(edge => nodes.find(n => n.id === edge.target));
      const totalConnected = connectedNodes.length;
      const parentX = node.position.x;
      const parentY = node.position.y;
  
      if (node.type === 'parallelHandler') {
        const parallelNode = nodes.find(n => n.type === 'parallel' && n.data.parentId === node.data.parentId);
        if (parallelNode) {
          node.position.x = parallelNode.position.x;
          // Adjust y position based on the edge
          const edgeIndex = connectedEdges.findIndex(edge => edge.target === node.id);
          if (edgeIndex !== -1) {
            node.position.y = parentY + spacingY * (edgeIndex + 1);
          }
        }
      } else {
        if (totalConnected === 1) {
          // Rule 1: Single edge
          const connectedNode = connectedNodes[0];
          connectedNode.position.x = parentX;
          connectedNode.position.y = parentY + spacingY;
        } else if (totalConnected === 2) {
          // Rule 2: Double edge
          connectedNodes.forEach((connectedNode, index) => {
            connectedNode.position.x = index === 0 ? parentX - spacingX : parentX + spacingX;
            connectedNode.position.y = parentY + spacingY;
          });
        } else if (totalConnected === 3) {
          // Rule 3: Three edges
          connectedNodes.forEach((connectedNode, index) => {
            connectedNode.position.x = index === 0? parentX - spacingX: index === 1? parentX: parentX + spacingX;
            connectedNode.position.y = parentY + spacingY;
          });
        } else if (node.type === 'parallel') {
          // Rule 4: Parallel node
          connectedNodes.forEach((connectedNode, index) => {
            connectedNode.position.x = parentX + (spacingX - (totalConnected / 2)) + index;
            connectedNode.position.y = parentY + spacingY;
          });
        }
      }
  
      return node;
    });
  
    setNodes(updatedNodes);
  }, [nodes, edges]);
  
  const createWorkflowFromExistingWorkflow=async()=>{
    const currentRemoteWorkflowCopy=currentRemoteWorkflow
    delete currentRemoteWorkflowCopy?.meta?.version
    const newWorkflow={ ...currentRemoteWorkflowCopy,
          meta: {
            ...currentRemoteWorkflowCopy.meta,
            status: WORKFLOW_IS_DRAFT,
            publishedBy: "",
            lastEdited: Date.now(),
            owner:userDetails?.email,
            s3Link:"",
            validFrom: "",
            createdBy: userDetails?.email,
            createdFrom: currentVersion
          }
     }
     const newDraft=await createDraft(newWorkflow)
     createWorkflowUrl(newDraft)
     setCurrentRemoteWorkflow(newDraft)
     setBit(prev=>!prev)
  }

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if(!searchParams.get("workflowId")){
        toast.error("Please select a workflow or create one from the Workflows section on the left.")
        return
      }
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");
      const workflowType=event.dataTransfer.getData("application/workflowType")
      if (typeof type === "undefined" || !type) return;
      const newNodeID = uuid();
      const newNode = {
        id: newNodeID,
        type,
        nodeType:workflowType,
        position: reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        }),
        data: {
          ...JSON.parse(event.dataTransfer.getData("data")),
          id: newNodeID,
          type,
        },
      };
      setNodes((currentNodes) => currentNodes.concat(newNode));
    },
    [reactFlowInstance]
  );

  const updateNode = useCallback(
    debounce((id, data) => {
      setNodes((oldNodes) =>
        oldNodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, ...data } } : node
        )
      );
    }, 250), // Adjust debounce delay as needed
    [] // Debounce function dependencies (none needed here since debounce creates a stable function)
  );

  const deleteNode = useCallback(
    (id) => {
      setNodes((oldNodes) => oldNodes.filter((node) => node.id !== id));
      setEdges((currentEdges) =>
        currentEdges.filter((edge) => ![edge.source, edge.target].includes(id))
      );
      dispatch(setSelectedNode());
    },
    []
  );

  const handleSelectedWorkflow = useCallback(
    (selectedWorkflowId, selectedWorkflowStage) => {
      if (selectedWorkflowId === id && selectedWorkflowStage === workflowStage) return;
      const selectedWorkflow = workflows.find((workflow) => {
        const currentWorkflowStage = determineWorkflowStage(workflow);
        if ( workflow.workflowId === selectedWorkflowId && currentWorkflowStage === selectedWorkflowStage )
          return workflow;
      });
      if (!selectedWorkflow) return;
      createWorkflowUrl(selectedWorkflow);
    },
    [workflows, createWorkflowUrl, id, determineWorkflowStage, workflowStage]
  );

  const handleDrawerClose = useCallback(() => {
    setOpenCollapse(false);
  },[]);

  const message = (
    <div>
      Are you sure you want to delete{" "}
      <b>{currentRemoteWorkflow?.meta?.workflowName}</b>?{" "}
    </div>
  );

  // Return:
  return (
    <Wrapper>
       <FormControlLabel
                  sx={{ position:'absolute', left:!isAutoFlow?'25%':'50%',marginTop:!isAutoFlow?'1%':'-0.5%',zIndex:'10'}}
                    control={
                      <Switch checked={isAutoFlow} onChange={()=>{setIsAutoFlow(!isAutoFlow)}} name="gilad" />
                    }
                    label={isAutoFlow?'Switch to Workflow':'Auto flow'}
                  />
      {isAutoFlow?<AutoFLow currentRemoteAutoFlow={currentRemoteAutoFlow} setCurrentRemoteAutoFlow={setCurrentRemoteAutoFlow} searchParams={searchParams} setSearchParams={setSearchParams} setBit={setBit} loadingWorkflows={loadingWorkflows} setIsAutoFlow={setIsAutoFlow}/>:<>
      <Tooltip title="Workflow Menu">
        <IconButton
          onClick={()=>setOpenCollapse(true)}
          sx={{
            mt: "10px",
            backgroundColor: "#FFFFFF",
            boxShadow:
              "0px 1px 3px 0px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 2px 1px -1px rgb(0 0 0 / 12%)",
            ...(openCollapse && { display: "none" }),
          }}
        >
          <MenuOutlinedIcon />
        </IconButton>
      </Tooltip>
      <ReactFlowProvider>
        <MainDiv>
          {openCollapse && (
            <SideBarCollapse>
              <Workflows
                handleDrawerClose={handleDrawerClose}
                openCollapse={openCollapse}
                isFetching={loadingWorkflows}
                isCreatingNewWorkflow={isCreatingNewWorkflow}
                workflows={workflows}
                currentWorkflowID={id}
                workflowStage={workflowStage}
                onClick={handleSelectedWorkflow}
                createNewWorkflow={initiateCreateNewWorkflow}
                setBit={setBit}
              />
            </SideBarCollapse>
          )}
          {currentRemoteWorkflow?.meta?.status === WORKFLOW_IS_DRAFT && (
            <Assets />
          )}
          <Canvas ref={reactFlowWrapper}>
            {loadingWorkflows? (
              <>
                <Title>Canvas</Title>
                <LoadingCanvas>
                  <CircularProgress />
                </LoadingCanvas>
              </>
            ) : (
              <>
                <Title>
                  {open && (
                    <CommentBox
                      open={open}
                      handleClose={handleClose}
                      agent_type={agent_type}
                      documentId={id}
                      handleAddComment={handleAddComment}
                      storeComments={workflowComments}
                      loading={fetchingComments}
                      changeCommentsArray={changeCommentsArray}
                      comments={comments}
                    />
                  )}
                  <ConfirmationDialog
                    title={"Delete Workflow"}
                    message={message}
                    performAction={initiateDeleteWorkflow}
                    openModal={openModal}
                    handleOpenModal={handleOpenModal}
                    handleCloseModal={handleCloseModal}
                    actionButton={"Delete"}
                  />
                  Canvas:
                  <InlineEdit title={name} name={name} setTitle={setName} getModifiedWorkflow={getModifiedWorkflow} currentRemoteWorkflow={currentRemoteWorkflow} nodes={nodes} edges={edges} setCurrentRemoteWorkflow={setCurrentRemoteWorkflow} />
                  {/* <FormControlLabel
                  sx={{marginLeft:'2rem'}}
                    control={
                      <Switch checked={isAutoFlow} onChange={()=>{setIsAutoFlow(!isAutoFlow)}} name="gilad" />
                    }
                    label={isAutoFlow?'Switch to Workflow':'Switch to Auto flow'}
                  /> */}
                  <ButtonGroup showOpt={showOpt}>
                      {agent_type !== "endUser" && (
                        <TooltipIconButton
                          title="Info"
                          onClick={() => handleClickOpen(orgId, id)}
                          icon={<InfoOutlinedIcon color="primary" sx={{ fontSize: 25 }} />}
                        />
                      )}
                      <TooltipIconButton
                        title="Delete"
                        onClick={handleOpenModal}
                        icon={<DeleteOutlineOutlinedIcon />}
                        color="error"
                      />
                      <TooltipIconButton
                        title="Submit for Review"
                        onClick={handleDraftSubmission}
                        icon={<DataSaverOnSharpIcon fontSize="small" />}
                        disabled={currentRemoteWorkflow?.meta?.status !== WORKFLOW_IS_DRAFT}
                        color="primary"
                      />
                      {(agent_type === "supervisor" || agent_type === "superadmin"|| agent_type.toLowerCase()==='admin') && (
                        <>
                          <TooltipIconButton
                            title="Publish"
                            onClick={async () => {
                              const modifiedWorkflow = getModifiedWorkflow(currentRemoteWorkflow, nodes, edges, name);
                              setCurrentRemoteWorkflow(modifiedWorkflow);
                              await handlePublish(modifiedWorkflow);
                            }}
                            icon={<PublishOutlinedIcon />}
                            disabled={currentRemoteWorkflow?.meta?.status !== WORKFLOW_IS_UNDER_REVIEW}
                            color="success"
                          />
                          <TooltipIconButton
                            title="Save Workflow"
                            onClick={() => saveWorkflow(currentRemoteWorkflow)}
                            icon={<SaveAsOutlinedIcon />}
                            color="success"
                          />
                          <TooltipIconButton
                            title="Equispace Nodes"
                            onClick={makeNodesEquispacedAndCentered}
                            icon={<VerticalAlignCenterIcon />}
                            color="success"
                          />
                        </>
                      )}
                      {currentRemoteWorkflow.meta.status === WORKFLOW_IS_PUBLISHED && (
                        <TooltipIconButton
                          title="Edit Workflow"
                          onClick={createWorkflowFromExistingWorkflow}
                          icon={<EditOutlinedIcon fontSize="small" />}
                          disabled={currentRemoteWorkflow?.meta?.status !== WORKFLOW_IS_PUBLISHED}
                          color="success"
                        />
                      )}
                    </ButtonGroup>

                </Title>
                <ReactFlow
                  onContextMenu={(e) => {
                    e.preventDefault();
                  }}
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={ currentRemoteWorkflow?.meta?.status === WORKFLOW_IS_DRAFT ? onConnect : () => {} }
                  onInit={setReactFlowInstance}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  zoomOnScroll={false}
                  panOnScroll={true}
                  onEdgesDelete={onEdgesDelete}             
                  defaultZoom={1}
                  style={{ height: "95%" }}
                  nodeTypes={nodeTypes}
                  nodesConnectable={ currentRemoteWorkflow?.meta?.status === WORKFLOW_IS_DRAFT}
                >
                  <Controls />
                  <MiniMap />
                </ReactFlow>
              </>
            )}
          </Canvas>
          <Configuration
            updateNode={updateNode}
            deleteNode={deleteNode}
            settings={currentRemoteWorkflow?.meta?.settings}
            currentRemoteWorkflow={currentRemoteWorkflow}
            setCurrentRemoteWorkflow={setCurrentRemoteWorkflow}
            isEditable={
              currentRemoteWorkflow?.meta?.status === WORKFLOW_IS_DRAFT
            }
            setEdges={setEdges}
            setNodes={setNodes}  
          />
        </MainDiv>
      </ReactFlowProvider>
      </>}
    </Wrapper>
  );
};

// Exports:
export default memo(Studio);
